import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
  MutableRefObject,
} from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { currentFeatures, oids } from "../configs/Settings";
import {
  BundlePatientSummary,
  GetAppointment,
  GetEncounter,
  GetPatient,
} from "../services/fhir";
import { useLocationContext } from "./LocationContext";
import { useSessionContext } from "./SessionContext";
import { useFhirData } from "../utils/useFhirData";
import { SessionHistory, ResponseHistoryItem } from "@/components/luxor/types";
import { LuxorMedResponse } from "@/components/luxor/utils/manageHistoryLuxorMed";
import { PostQuestionnaireResponse } from "@/services/fhir/patientsummary/questionnaireResponse/PostQuestionnaireResponse";
import { GetVetPatient } from "@/services/fhir/vetpatient/GetVetPatient";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";

interface MedicalCareContextValue {
  setActiveStep: Function;
  activeStep: any;
  setRegisterAreaTab: Function;
  registerAreaTab: any;
  setProblems: Function;
  problems: any;
  setCondutas: Function;
  condutas: any;
  setCarePlan: Function;
  carePlan: any;
  setNewExams: Function;
  newExams: any;
  setPastExams: Function;
  pastExams: any;
  setInterventionTab: Function;
  interventionTab: any;
  setOpenDialog: Function;
  openDialog: any;
  setOpenSummary: Function;
  openSummary: any;
  loadingSummary: boolean;
  loadingAppointment: boolean;
  loadingDiagnostics: boolean;
  loadingTreatments: boolean;
  setLoadingDiagnostics: Function;
  setLoadingTreatments: Function;
  summaryData: any;
  type: any;
  patient: any;
  appointment: any;
  encounter: fhir5.Encounter | undefined;
  tutorData: fhir5.Patient | null; // Dados do tutor (apenas para modo veterinário)
  setType: any;
  setRefetch: any;
  control: any;
  handleSubmit: any;
  unregister: any;
  setValue: any;
  initial: any;
  resetSignal: number;
  clinicalLocation: fhir5.Location | undefined;
  clearRegisterData: any;
  isNursingService: boolean;
  healthcareServiceDisplay: string | undefined;
  luxorMode: boolean;
  setLuxorMode: Function;
  isPatientQuestionnairesPanelOpen: boolean;
  setIsPatientQuestionnairesPanelOpen: Function;
  copyMedicalData: any;
  setCopyMedicalData: Function;
  getValues: any;
  luxorSessionsHistory: Record<string, SessionHistory>;
  setLuxorSessionsHistory: React.Dispatch<
    React.SetStateAction<Record<string, SessionHistory>>
  >;
  luxorResponseHistory: ResponseHistoryItem[];
  setLuxorResponseHistory: React.Dispatch<
    React.SetStateAction<ResponseHistoryItem[]>
  >;
  luxorCurrentSession: string;
  setLuxorCurrentSession: React.Dispatch<React.SetStateAction<string>>;
  luxorShowEditor: boolean;
  setLuxorShowEditor: React.Dispatch<React.SetStateAction<boolean>>;
  luxorAppointmentHistory: LuxorMedResponse[];
  addLuxorResponse: (response: LuxorMedResponse) => void;
  sendLuxorHistoryAsQuestionnaireResponse: () => Promise<void>;
  // Permite que o botão "Finalizar atendimento" seja acionado fora do Footer
  // (ex.: no StepArea). O Footer registra a ação neste ref e sincroniza o loading.
  finalizeAttendanceRef: MutableRefObject<() => void>;
  isFinalizingAttendance: boolean;
  setIsFinalizingAttendance: Function;
}

export const MedicalCareContext = createContext<MedicalCareContextValue>(null!);

export function MedicalCareContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state } = useLocation();
  const { user, access } = useSessionContext();
  const { location } = useLocationContext();
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false);
  const [loadingAppointment, setLoadingAppointment] = useState<boolean>(false);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState<boolean>(false);
  const [loadingTreatments, setLoadingTreatments] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState();
  const [patient, setPatient] = useState<fhir5.Patient>();
  const [appointment, setAppointment] = useState<fhir5.Appointment>();
  const [encounter, setEncounter] = useState<fhir5.Encounter>();
  const [tutorData, setTutorData] = useState<fhir5.Patient | null>(null);

  const [newExams, setNewExams] = useState<any>([]);
  const [pastExams, setPastExams] = useState<any>([]);

  const [problems, setProblems] = useState<any>([]);

  const [condutas, setCondutas] = useState<any>([]);
  const [carePlan, setCarePlan] = useState<any>([""]);
  const [initial, setInitial] = useState<boolean>(true);
  /**
   * Sinal de reset do atendimento. Diferente de `initial`, cujo valor pode
   * repetir (setInitial(true) sobre true é no-op e não dispara efeitos), este
   * contador SEMPRE muda a cada clearRegisterData — é o que permite limpar
   * intervenções/protocolos/PDFs do 3º atendimento em diante na mesma sessão.
   */
  const [resetSignal, setResetSignal] = useState<number>(0);

  const [activeStep, setActiveStep] = useState(0);
  const [registerAreaTab, setRegisterAreaTab] = useState(0);
  const [interventionTab, setInterventionTab] = useState(0);
  const [openSummary, setOpenSummary] = useState<boolean>(false);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [luxorMode, setLuxorMode] = useState<boolean>(false);
  const [isPatientQuestionnairesPanelOpen, setIsPatientQuestionnairesPanelOpen] =
    useState<boolean>(false);
  const [copyMedicalData, setCopyMedicalData] = useState<any>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [type, setType] = useState<any>(0);

  const [luxorSessionsHistory, setLuxorSessionsHistory] = useState<
    Record<string, SessionHistory>
  >({});
  const [luxorResponseHistory, setLuxorResponseHistory] = useState<
    ResponseHistoryItem[]
  >([]);

  const [luxorCurrentSession, setLuxorCurrentSession] = useState<string>(
    "luxor-patient-case-analysis"
  );

  const [luxorShowEditor, setLuxorShowEditor] = useState<boolean>(true);

  const [luxorAppointmentHistory, setLuxorAppointmentHistory] = useState<
    LuxorMedResponse[]
  >([]);

  // Ação de finalizar atendimento registrada pelo Footer e acionável pelo StepArea.
  const finalizeAttendanceRef = useRef<() => void>(() => {});
  const [isFinalizingAttendance, setIsFinalizingAttendance] =
    useState<boolean>(false);

  const methods = useForm();
  const { handleSubmit, control, unregister, setValue, reset, getValues } =
    methods;

  /**
   * Chave composta do atendimento em `location.state`.
   *
   * Trocar de atendimento pelo "Sumário de saúde" navega de /clinicalregister
   * para /clinicalregister: o subject não muda, só o appointment/encounter.
   * Efeitos presos apenas ao subject não disparavam e a tela seguia com os
   * dados do atendimento anterior.
   */
  const attendanceKey = [
    state?.encounterReference?.subject,
    state?.encounterReference?.appointmentId,
    state?.encounterReference?.id,
  ]
    .map((part: any) => part ?? "")
    .join("|");

  const clinicalLocation = location?.find((el) =>
    el?.resource?.id?.includes(
      state?.encounterReference?.participant
        ?.find((loc: any) => loc?.actor?.reference?.includes("Location"))
        ?.actor?.reference?.split("/")?.[1]
    )
  )?.resource;

  const addLuxorResponse = (response: LuxorMedResponse) => {
    setLuxorAppointmentHistory((prev) => [...prev, response]);
  };

  const sendLuxorHistoryAsQuestionnaireResponse = async () => {
    if (luxorAppointmentHistory.length === 0) {
      console.log("Nenhuma resposta do LuxorMed para enviar");
      return;
    }

    try {
      const questionnaireResponse = {
        resourceType: "QuestionnaireResponse",
        questionnaire: "Questionnaire/luxor-med-history",
        status: "completed",
        authored: new Date().toISOString(),
        subject: {
          reference: `Patient/${patient?.id}`,
        },
        author: {
          reference: `Practitioner/${oids.cpf}-${user.username}`,
        },
        encounter: {
          reference: `Encounter/${state?.encounterReference?.id}`,
        },
        item: luxorAppointmentHistory.map((response) => ({
          linkId: "luxor-response-group",
          text: "Respostas do LuxorMed",
          item: [
            {
              linkId: "agent-type",
              text: "Tipo de Agente",
              answer: [
                {
                  valueString: response.responseType,
                },
              ],
            },
            {
              linkId: "response-content",
              text: "Conteúdo da Resposta",
              answer: [
                {
                  valueString: response.responseContent,
                },
              ],
            },
            {
              linkId: "question-content",
              text: "Conteúdo da Pergunta",
              answer: [
                {
                  valueString: response.promptContent,
                },
              ],
            },
            {
              linkId: "prompt-version",
              text: "Versão do Prompt",
              answer: [
                {
                  valueString: response.promptVersion || "1.0.0",
                },
              ],
            },
            {
              linkId: "response-timestamp",
              text: "Data e Hora",
              answer: [
                {
                  valueDateTime: response.timestamp,
                },
              ],
            },
            {
              linkId: "agent-provider",
              text: "Fornecedor",
              answer: [
                {
                  valueString: response.agentProvider || "LuxorMed",
                },
              ],
            },
            ...(response.appointmentId
              ? [
                  {
                    linkId: "appointment-id",
                    text: "ID do Atendimento",
                    answer: [
                      {
                        valueString: response.appointmentId,
                      },
                    ],
                  },
                ]
              : []),
          ],
        })),
      };

      console.log(
        "Enviando QuestionnaireResponse do LuxorMed:",
        questionnaireResponse
      );

      const result = await PostQuestionnaireResponse(questionnaireResponse);
      console.log("QuestionnaireResponse enviado com sucesso:", result);

      setLuxorAppointmentHistory([]);
    } catch (error) {
      console.error("Erro ao enviar QuestionnaireResponse do LuxorMed:", error);
      throw error;
    }
  };

  useEffect(() => {
    async function GetBundle() {
      setLoadingSummary(true);
      try {
        const bundleResponse = await BundlePatientSummary({
          identifier: state?.encounterReference?.subject,
        });
        setSummaryData(bundleResponse);
      } catch (err) {
        console.log("PatientSummary error:", err);
      } finally {
        setLoadingSummary(false);
        setLoadingDiagnostics(false);
        setLoadingTreatments(false);
        setRefetch(false);
      }
    }

    if (state?.encounterReference?.subject && access?.type !== "patient") {
      GetBundle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceKey, refetch]);

  function clearRegisterData() {
    setActiveStep(0);
    setRegisterAreaTab(0);
    setInterventionTab(0);
    reset();
    setNewExams([]);
    setPastExams([]);
    setProblems([]);
    setCondutas([]);
    setCarePlan([""]);
    setCopyMedicalData([]);
    setLuxorSessionsHistory({});
    setLuxorResponseHistory([]);
    setLuxorAppointmentHistory([]);
    setLuxorShowEditor(true);
    setLuxorMode(false);
    // InterventionsContext está abaixo deste provider em AppProviders (não pode
    // ser reordenado): ele limpa lista/protocolos/PDFs consumindo este sinal.
    setResetSignal((prev) => prev + 1);
  }

  /**
   * Busca todos os dados necessários para o atendimento médico
   * Inclui: Appointment, Patient (ou Pet) e Tutor (se veterinário)
   * Usa Promise.all para otimizar as chamadas em paralelo
   */
  const fetchAttendanceData = useCallback(async () => {
    setLoadingAppointment(true);
    setActiveStep(0);

    try {
      // Verifica se precisa limpar dados ao trocar de appointment
      if (
        appointment &&
        appointment?.id !== state?.encounterReference?.appointmentId
      ) {
        clearRegisterData();
        setInitial(true);
      } else {
        setInitial(false);
      }

      // Passo 1: Busca dados iniciais em paralelo
      const promises: Promise<any>[] = [
        // Busca o appointment
        GetAppointment({
          _id: state?.encounterReference?.appointmentId,
          includePatient: true,
          actor: `Practitioner/${oids.cpf}-${user.username}`,
        }),
        // Busca o paciente (humano) ou pet (veterinário)
        currentFeatures?.defaultApp
          ? GetPatient({
              identifier: state?.encounterReference?.subject,
            })
          : GetVetPatient({
              id: state?.encounterReference?.subject,
            }),
        // Encounter com actualPeriod.start (origem do timer do atendimento)
        GetEncounter({
          patientId: state?.encounterReference?.subject,
          appointmentId: state?.encounterReference?.appointmentId,
          lastUpdaded: true,
          count: 1,
        }),
      ];

      // Se for veterinário, adiciona busca do RelatedPerson
      if (!currentFeatures?.defaultApp) {
        promises.push(
          GetRelatedPerson({
            patient: state?.encounterReference?.subject,
          })
        );
      }

      const results = await Promise.all(promises);

      const [
        appointmentResponse,
        patientResponse,
        encounterResponse,
        relatedPersonResponse,
      ] = results;

      // Processa e seta o appointment
      const auxAppointment = appointmentResponse?.entry?.find(
        (el: any) => el.resource.resourceType === "Appointment"
      );
      setAppointment(auxAppointment?.resource);

      const encounterId = state?.encounterReference?.id;
      const auxEncounter =
        encounterResponse?.entry?.find(
          (el: any) => el.resource?.id === encounterId
        ) ||
        encounterResponse?.entry?.find(
          (el: any) => el.resource?.resourceType === "Encounter"
        );
      setEncounter(
        auxEncounter?.resource?.resourceType === "Encounter"
          ? auxEncounter.resource
          : undefined
      );

      // Seta os dados do paciente/pet
      setPatient(patientResponse?.data);

      // Passo 2: Se for veterinário, busca dados do tutor
      if (!currentFeatures?.defaultApp && relatedPersonResponse) {
        const relatedPersonData = relatedPersonResponse?.data;

        if (relatedPersonData?.entry && relatedPersonData.entry.length > 0) {
          const relatedPerson = relatedPersonData.entry[0].resource;
          const tutorIdentifier = relatedPerson?.identifier?.[0]?.value;

          if (tutorIdentifier) {
            // Busca os dados completos do tutor usando o identifier (CPF)
            const tutorPatientData = await GetPatient({
              identifier: tutorIdentifier,
            });

            // Seta o tutorData com a resposta do GetPatient
            setTutorData(tutorPatientData?.data || null);
          } else {
            console.warn("Identifier do tutor não encontrado no RelatedPerson");
            setTutorData(null);
          }
        } else {
          console.warn("Nenhum RelatedPerson encontrado para o pet");
          setTutorData(null);
        }
      } else {
        // Se for modo humano, não tem tutor
        setTutorData(null);
      }
    } catch (err) {
      console.error("Erro ao carregar dados do atendimento:", err);
      setPatient(undefined);
      setAppointment(undefined);
      setEncounter(undefined);
      setTutorData(null);
    } finally {
      setLoadingAppointment(false);
    }
  }, [
    state?.encounterReference?.subject,
    state?.encounterReference?.appointmentId,
    user.username,
    appointment,
  ]);

  useEffect(() => {
    if (state?.encounterReference?.subject && access?.type !== "patient") {
      fetchAttendanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceKey]);

  const healthcareService = useMemo(() => {
    return useFhirData.findParticipant(
      appointment?.participant,
      "HealthcareService"
    );
  }, [appointment]);

  const isNursingService = useMemo(() => {
    return (
      healthcareService?.display.includes("Enfermeiro") ||
      healthcareService?.display === "Enfermagem"
    );
  }, [healthcareService]);

  const contextValue = {
    activeStep,
    setActiveStep,
    control,
    handleSubmit,
    openDialog,
    setOpenDialog,
    openSummary,
    setOpenSummary,
    type,
    setType,
    setRefetch,
    summaryData,
    patient,
    appointment,
    encounter,
    tutorData, // Dados do tutor (apenas para modo veterinário)
    loadingSummary,
    loadingAppointment,
    loadingDiagnostics,
    loadingTreatments,
    setLoadingTreatments,
    setLoadingDiagnostics,
    unregister,
    setValue,
    clinicalLocation,
    registerAreaTab,
    setRegisterAreaTab,
    interventionTab,
    setInterventionTab,
    newExams,
    setNewExams,
    pastExams,
    setPastExams,
    condutas,
    setCondutas,
    problems,
    setProblems,
    carePlan,
    setCarePlan,
    initial,
    resetSignal,
    clearRegisterData,
    isNursingService,
    healthcareServiceDisplay: healthcareService?.display,
    luxorMode,
    setLuxorMode,
    isPatientQuestionnairesPanelOpen,
    setIsPatientQuestionnairesPanelOpen,
    copyMedicalData,
    setCopyMedicalData,
    getValues,
    luxorSessionsHistory,
    setLuxorSessionsHistory,
    luxorResponseHistory,
    setLuxorResponseHistory,
    luxorCurrentSession,
    setLuxorCurrentSession,
    luxorShowEditor,
    setLuxorShowEditor,
    luxorAppointmentHistory,
    addLuxorResponse,
    sendLuxorHistoryAsQuestionnaireResponse,
    finalizeAttendanceRef,
    isFinalizingAttendance,
    setIsFinalizingAttendance,
  };

  return (
    <MedicalCareContext.Provider value={contextValue}>
      {children}
    </MedicalCareContext.Provider>
  );
}

export function useMedicalCareContext() {
  return useContext(MedicalCareContext);
}
